import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DESIGN, INITIAL_STATE, LIMITS, STORAGE_KEYS } from '../constants';
import { loadJSON, saveJSON } from '../lib/storage';
import type { EditorState, LayerId, Ratio, TextLayer } from '../types';

interface History {
  past: EditorState[];
  present: EditorState;
  future: EditorState[];
}

export interface UpdateOptions {
  /**
   * Continuous controls (sliders, colour pickers, typing) pass a tag so a burst
   * of changes collapses into a single undo step instead of ~100 of them.
   */
  tag?: string;
}

function mergeLayer(base: TextLayer, patch: unknown): TextLayer {
  return typeof patch === 'object' && patch !== null
    ? { ...base, ...(patch as Partial<TextLayer>) }
    : base;
}

/** Deep-merges a persisted snapshot over the defaults so older saves still load. */
function mergeState(stored: unknown): EditorState {
  if (typeof stored !== 'object' || stored === null) return structuredClone(INITIAL_STATE);
  const input = stored as Partial<EditorState>;

  return {
    ratio: input.ratio === '9:16' ? '9:16' : '16:9',
    backgroundColor: input.backgroundColor ?? INITIAL_STATE.backgroundColor,
    main: mergeLayer(INITIAL_STATE.main, input.main),
    sub: mergeLayer(INITIAL_STATE.sub, input.sub),
    filters: { ...INITIAL_STATE.filters, ...input.filters },
    frame: { ...INITIAL_STATE.frame, ...input.frame },
  };
}

/**
 * Every length is expressed in the design grid of the current ratio, and that
 * grid is 1280 px wide in landscape but 1080 px in portrait. Rescaling on a
 * ratio switch keeps the composition proportional instead of letting a headline
 * sized for YouTube overflow a Shorts cover.
 */
function rescaleLayer(layer: TextLayer, factor: number): TextLayer {
  const round = (value: number) => Math.round(value * factor);
  return {
    ...layer,
    fontSize: Math.max(1, round(layer.fontSize)),
    x: round(layer.x),
    y: round(layer.y),
    letterSpacing: round(layer.letterSpacing),
    strokeWidth: Math.max(1, round(layer.strokeWidth)),
    shadowBlur: round(layer.shadowBlur),
    backgroundPadding: round(layer.backgroundPadding),
    backgroundRadius: round(layer.backgroundRadius),
  };
}

function rescaleState(state: EditorState, ratio: Ratio): EditorState {
  if (state.ratio === ratio) return state;
  const factor = DESIGN[ratio].width / DESIGN[state.ratio].width;
  return {
    ...state,
    ratio,
    main: rescaleLayer(state.main, factor),
    sub: rescaleLayer(state.sub, factor),
    filters: {
      ...state.filters,
      blur: Number((state.filters.blur * factor).toFixed(1)),
    },
    frame: {
      ...state.frame,
      width: Math.round(state.frame.width * factor),
      radius: Math.round(state.frame.radius * factor),
    },
  };
}

const COALESCE_WINDOW_MS = 600;

export function useEditor() {
  // Read storage exactly once, and keep "did we restore?" in state rather than a
  // ref so it is safe to read while rendering.
  const [initial] = useState(() => {
    const stored = loadJSON<EditorState>(STORAGE_KEYS.editor);
    return { state: mergeState(stored), restored: stored !== null };
  });
  const [history, setHistory] = useState<History>(() => ({
    past: [],
    present: initial.state,
    future: [],
  }));

  const lastTag = useRef<{ tag: string; at: number } | null>(null);

  const update = useCallback(
    (producer: (prev: EditorState) => EditorState, options: UpdateOptions = {}) => {
      setHistory((current) => {
        const next = producer(current.present);
        if (next === current.present) return current;

        const now = Date.now();
        const tag = options.tag;
        const coalesce =
          tag !== undefined &&
          lastTag.current?.tag === tag &&
          now - lastTag.current.at < COALESCE_WINDOW_MS;
        lastTag.current = tag === undefined ? null : { tag, at: now };

        if (coalesce) {
          // Same gesture still in progress: replace the present, keep history flat.
          return { ...current, present: next, future: [] };
        }

        const past = [...current.past, current.present].slice(-LIMITS.historyLimit);
        return { past, present: next, future: [] };
      });
    },
    [],
  );

  const undo = useCallback(() => {
    lastTag.current = null;
    setHistory((current) => {
      const previous = current.past.at(-1);
      if (previous === undefined) return current;
      return {
        past: current.past.slice(0, -1),
        present: previous,
        future: [current.present, ...current.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    lastTag.current = null;
    setHistory((current) => {
      const [next, ...rest] = current.future;
      if (next === undefined) return current;
      return { past: [...current.past, current.present], present: next, future: rest };
    });
  }, []);

  const reset = useCallback(() => {
    lastTag.current = null;
    setHistory((current) => ({
      past: [...current.past, current.present].slice(-LIMITS.historyLimit),
      present: structuredClone(INITIAL_STATE),
      future: [],
    }));
  }, []);

  const setLayer = useCallback(
    (id: LayerId, patch: Partial<TextLayer>, options?: UpdateOptions) => {
      update((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }), options);
    },
    [update],
  );

  const setRatio = useCallback(
    (ratio: Ratio) => {
      update((prev) => rescaleState(prev, ratio));
    },
    [update],
  );

  const setFilters = useCallback(
    (patch: Partial<EditorState['filters']>, options?: UpdateOptions) => {
      update((prev) => ({ ...prev, filters: { ...prev.filters, ...patch } }), options);
    },
    [update],
  );

  const setFrame = useCallback(
    (patch: Partial<EditorState['frame']>, options?: UpdateOptions) => {
      update((prev) => ({ ...prev, frame: { ...prev.frame, ...patch } }), options);
    },
    [update],
  );

  const applyPatch = useCallback(
    (patch: Partial<EditorState>) => {
      update((prev) => ({
        ...prev,
        ...patch,
        // Style patches never overwrite what the user actually typed.
        main: patch.main ? { ...patch.main, text: prev.main.text } : prev.main,
        sub: patch.sub ? { ...patch.sub, text: prev.sub.text } : prev.sub,
      }));
    },
    [update],
  );

  // Autosave, debounced so dragging a slider does not hammer localStorage.
  const state = history.present;
  useEffect(() => {
    const timer = setTimeout(() => saveJSON(STORAGE_KEYS.editor, state), 400);
    return () => clearTimeout(timer);
  }, [state]);

  return useMemo(
    () => ({
      state,
      update,
      setLayer,
      setRatio,
      setFilters,
      setFrame,
      applyPatch,
      undo,
      redo,
      reset,
      canUndo: history.past.length > 0,
      canRedo: history.future.length > 0,
      restoredFromStorage: initial.restored,
    }),
    [
      state,
      update,
      setLayer,
      setRatio,
      setFilters,
      setFrame,
      applyPatch,
      undo,
      redo,
      reset,
      history.past.length,
      history.future.length,
      initial.restored,
    ],
  );
}

export type EditorApi = ReturnType<typeof useEditor>;
